import { createFileRoute, notFound } from "@tanstack/react-router";
import { BlogListingPage } from "@/components/site/BlogListingPage";
import { blogPosts } from "@/data/blog-posts";
import { breadcrumbSchema, jsonLdScript, organizationSchema } from "@/lib/seo";

const POSTS_PER_PAGE = 8;

type PageSeo = {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
};

// Each numbered page has its own editable metadata entry. Add another entry
// here when the article count creates a new pagination page.
const PAGE_SEO: Record<number, PageSeo> = {
  2: {
    title: "Blog - Page 2",
    description: "Browse page 2 of our blog for law firm SEO guides covering strategy, local search, content, and sustainable growth.",
    ogTitle: "Blog - Page 2 | PageOne Lawyers",
    ogDescription: "Explore more practical law firm SEO articles and resources from PageOne Lawyers.",
  },
};

function parsePage(value: string) {
  if (!/^\d+$/.test(value)) return null;
  const page = Number(value);
  return Number.isSafeInteger(page) ? page : null;
}

export const Route = createFileRoute("/blog/page/$page")({
  beforeLoad: ({ params }) => {
    const currentPage = parsePage(params.page);
    const totalPages = Math.ceil(blogPosts.length / POSTS_PER_PAGE);
    if (!currentPage || currentPage < 2 || currentPage > totalPages) throw notFound();
    return { currentPage };
  },
  head: ({ params }) => {
    const currentPage = parsePage(params.page) ?? 2;
    const canonical = `https://pageonelawyers.com/blog/page/${currentPage}`;
    const seo = PAGE_SEO[currentPage] ?? {
      title: `Blog - Page ${currentPage}`,
      description: `Browse page ${currentPage} of our blog for the latest articles on law firm SEO, local search, legal marketing, and AI search.`,
      ogTitle: `Blog - Page ${currentPage} | PageOne Lawyers`,
      ogDescription: `Explore page ${currentPage} of practical law firm SEO articles and resources from PageOne Lawyers.`,
    };

    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: seo.ogTitle },
        { property: "og:description", content: seo.ogDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        jsonLdScript(organizationSchema()),
        jsonLdScript(
          breadcrumbSchema([
            { label: "Home", path: "/" },
            { label: "Blog", path: "/blog" },
            { label: `Page ${currentPage}`, path: `/blog/page/${currentPage}` },
          ]),
        ),
      ],
    };
  },
  component: PaginatedBlogPage,
});

function PaginatedBlogPage() {
  const { currentPage } = Route.useRouteContext();
  return <BlogListingPage currentPage={currentPage} />;
}